import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import { Checkbox } from '../atoms';
import type {
  SplashScreen,
  SplashScreenCreateRequest,
  SplashScreenUpdateRequest,
} from '../../types/api/splash-screen';
import {
  TARGET_PLATFORM_OPTIONS,
  LANGUAGE_OPTIONS,
} from '../../types/api/splash-screen';

export interface SplashScreenFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: SplashScreenCreateRequest | SplashScreenUpdateRequest,
    imageFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  splashScreen?: SplashScreen;
}

type SplashScreenFormState = {
  title: string;
  description: string;
  imageUrl: string;
  primaryCtaText: string;
  primaryCtaAction: string;
  secondaryCtaText: string;
  secondaryCtaAction: string;
  isSkippable: boolean;
  sequenceOrder: number;
  durationSeconds: string;
  contentReferenceId: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  targetPlatform: string;
  language: string;
};

const createDefaultState = (): SplashScreenFormState => ({
  title: '',
  description: '',
  imageUrl: '',
  primaryCtaText: '',
  primaryCtaAction: '',
  secondaryCtaText: '',
  secondaryCtaAction: '',
  isSkippable: true,
  sequenceOrder: 1,
  durationSeconds: '',
  contentReferenceId: '',
  isActive: true,
  startDate: '',
  endDate: '',
  targetPlatform: 'all',
  language: 'en',
});

export const SplashScreenFormModal: React.FC<SplashScreenFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  splashScreen,
}) => {
  const [formData, setFormData] = useState<SplashScreenFormState>(createDefaultState());
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (splashScreen && mode === 'edit' && isOpen) {
      setFormData({
        title: splashScreen.title || '',
        description: splashScreen.description ?? '',
        imageUrl: splashScreen.imageUrl ?? '',
        primaryCtaText: splashScreen.primaryCtaText ?? '',
        primaryCtaAction: splashScreen.primaryCtaAction ?? '',
        secondaryCtaText: splashScreen.secondaryCtaText ?? '',
        secondaryCtaAction: splashScreen.secondaryCtaAction ?? '',
        isSkippable: splashScreen.isSkippable ?? true,
        sequenceOrder: splashScreen.sequenceOrder ?? 1,
        durationSeconds: splashScreen.durationSeconds != null ? String(splashScreen.durationSeconds) : '',
        contentReferenceId: splashScreen.contentReferenceId ?? '',
        isActive: splashScreen.isActive ?? true,
        startDate: splashScreen.startDate ? splashScreen.startDate.substring(0, 16) : '',
        endDate: splashScreen.endDate ? splashScreen.endDate.substring(0, 16) : '',
        targetPlatform: splashScreen.targetPlatform || 'all',
        language: splashScreen.language || 'en',
      });
      setImagePreview(splashScreen.imageUrl || null);
      setImageFile(null);
    } else if (isOpen) {
      setFormData(createDefaultState());
      setImagePreview(null);
      setImageFile(null);
    }
    setErrors({});
  }, [splashScreen, mode, isOpen]);

  const handleChange = (field: keyof SplashScreenFormState, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, image: 'Please select a valid image file' }));
      return;
    }

    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: `Image size must be less than ${maxSizeMb}MB` }));
      return;
    }

    setImageFile(file);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.image;
      return next;
    });

    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title must be 255 characters or less';
    }

    if (formData.description.length > 5000) {
      newErrors.description = 'Description must be 5000 characters or less';
    }

    if (mode === 'create') {
      if (!imageFile && !formData.imageUrl.trim() && !imagePreview) {
        newErrors.image = 'Upload an image or enter an image URL';
      }
      if (!imageFile && formData.imageUrl.trim() && formData.imageUrl.length > 500) {
        newErrors.imageUrl = 'Image URL must be 500 characters or less';
      }
    }

    if (formData.primaryCtaText.length > 100) {
      newErrors.primaryCtaText = 'Primary CTA text must be 100 characters or less';
    }
    if (formData.primaryCtaAction.length > 500) {
      newErrors.primaryCtaAction = 'Primary CTA action must be 500 characters or less';
    }
    if (formData.secondaryCtaText.length > 100) {
      newErrors.secondaryCtaText = 'Secondary CTA text must be 100 characters or less';
    }
    if (formData.secondaryCtaAction.length > 500) {
      newErrors.secondaryCtaAction = 'Secondary CTA action must be 500 characters or less';
    }

    if (formData.sequenceOrder < 0) {
      newErrors.sequenceOrder = 'Sequence order must be 0 or greater';
    }

    if (formData.durationSeconds !== '') {
      const n = parseInt(formData.durationSeconds, 10);
      if (isNaN(n) || n < 0) {
        newErrors.durationSeconds = 'Duration must be a non-negative number';
      }
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        newErrors.endDate = 'End date must be on or after start date';
      }
    }

    if (formData.targetPlatform.length > 50) {
      newErrors.targetPlatform = 'Target platform must be 50 characters or less';
    }
    if (formData.language.length > 10) {
      newErrors.language = 'Language must be 10 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload: SplashScreenCreateRequest | SplashScreenUpdateRequest = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        primaryCtaText: formData.primaryCtaText.trim() || null,
        primaryCtaAction: formData.primaryCtaAction.trim() || null,
        secondaryCtaText: formData.secondaryCtaText.trim() || null,
        secondaryCtaAction: formData.secondaryCtaAction.trim() || null,
        isSkippable: formData.isSkippable,
        sequenceOrder: formData.sequenceOrder,
        durationSeconds:
          formData.durationSeconds !== ''
            ? parseInt(formData.durationSeconds, 10)
            : null,
        contentReferenceId: formData.contentReferenceId.trim() || null,
        isActive: formData.isActive,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        targetPlatform: formData.targetPlatform.trim() || 'all',
        language: formData.language.trim() || 'en',
      };

      if (mode === 'create' && formData.imageUrl.trim() && !imageFile) {
        (payload as SplashScreenCreateRequest).imageUrl = formData.imageUrl.trim();
      }

      await onSubmit(payload, imageFile ?? null);
      handleClose();
    } catch (error) {
      console.error('Splash screen form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(createDefaultState());
      setErrors({});
      setImageFile(null);
      setImagePreview(null);
      onClose();
    }
  };

  const displayPreview = imagePreview || (formData.imageUrl.trim() ? formData.imageUrl : null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Create Splash Screen' : 'Edit Splash Screen'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Basic Information</p>

          <FormField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="e.g., Welcome to Phoewa"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              placeholder="Optional description (max 5000 chars)"
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1 text-left">{errors.description}</p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Image</p>
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
              Image {mode === 'create' && <span className="text-red-500">*</span>}
            </label>
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="w-32 h-20 rounded bg-gray-100 flex items-center justify-center overflow-hidden border flex-shrink-0">
                  {displayPreview ? (
                    <img
                      src={displayPreview}
                      alt="Splash preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No image</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="block text-sm text-gray-700"
                  />
                  <p className="mt-1 text-xs text-gray-500">JPEG, PNG or WebP. Max 5MB.</p>
                  {errors.image && (
                    <p className="text-sm text-red-600 mt-1.5">{errors.image}</p>
                  )}
                </div>
              </div>
              {mode === 'create' && (
                <FormField
                  label="Or enter image URL (if not uploading a file)"
                  value={formData.imageUrl}
                  onChange={(e) => handleChange('imageUrl', e.target.value)}
                  error={errors.imageUrl}
                  placeholder="https://..."
                />
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Call to Action</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Primary CTA Text"
              value={formData.primaryCtaText}
              onChange={(e) => handleChange('primaryCtaText', e.target.value)}
              error={errors.primaryCtaText}
              placeholder="e.g., Get Started"
            />
            <FormField
              label="Primary CTA Action"
              value={formData.primaryCtaAction}
              onChange={(e) => handleChange('primaryCtaAction', e.target.value)}
              error={errors.primaryCtaAction}
              placeholder="e.g., phoewa://home"
            />
            <FormField
              label="Secondary CTA Text"
              value={formData.secondaryCtaText}
              onChange={(e) => handleChange('secondaryCtaText', e.target.value)}
              error={errors.secondaryCtaText}
              placeholder="e.g., Skip"
            />
            <FormField
              label="Secondary CTA Action"
              value={formData.secondaryCtaAction}
              onChange={(e) => handleChange('secondaryCtaAction', e.target.value)}
              error={errors.secondaryCtaAction}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-2xl font-semibold text-gray-700 text-left">Display &amp; Schedule</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Sequence Order"
              type="number"
              value={formData.sequenceOrder}
              onChange={(e) => handleChange('sequenceOrder', parseInt(e.target.value, 10) || 0)}
              error={errors.sequenceOrder}
              min={0}
            />
            <FormField
              label="Duration (seconds)"
              value={formData.durationSeconds}
              onChange={(e) => handleChange('durationSeconds', e.target.value)}
              error={errors.durationSeconds}
              placeholder="Auto-advance after N seconds"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Target Platform
              </label>
              <select
                value={formData.targetPlatform}
                onChange={(e) => handleChange('targetPlatform', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              >
                {TARGET_PLATFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Language
              </label>
              <select
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <FormField
              label="Start Date"
              type="datetime-local"
              value={formData.startDate}
              onChange={(e) => handleChange('startDate', e.target.value)}
              error={errors.startDate}
            />
            <FormField
              label="End Date"
              type="datetime-local"
              value={formData.endDate}
              onChange={(e) => handleChange('endDate', e.target.value)}
              error={errors.endDate}
            />
          </div>
          <FormField
            label="Content Reference ID (UUID)"
            value={formData.contentReferenceId}
            onChange={(e) => handleChange('contentReferenceId', e.target.value)}
            placeholder="Optional linked content"
          />
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <Checkbox
                checked={formData.isSkippable}
                onChange={(e) => handleChange('isSkippable', e.target.checked)}
              />
              <label className="ml-2 text-sm text-gray-700">Skippable</label>
            </div>
            <div className="flex items-center">
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
              <label className="ml-2 text-sm text-gray-700">Active</label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" onClick={handleClose} disabled={isSubmitting} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Splash Screen' : 'Update Splash Screen'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
